import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ModalPortal from './ModalPortal';

const ConfirmContext = createContext(null);

/**
 * Styled replacement for window.confirm and window.prompt (UX-03).
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm('Delete this?'))) return;
 *   if (!(await confirm({ title: 'Delete asset', message: '…', danger: true }))) return;
 *
 * Ask for text by passing `prompt`. The promise then resolves to the typed
 * string rather than `true`, and still to `false` on cancel — so the usual
 * `if (!(await confirm(...))) return;` guard keeps working either way:
 *
 *   const why = await confirm({
 *     title: 'Reopen this bug',
 *     prompt: { label: 'What is happening now?', multiline: true, required: true },
 *   });
 *   if (!why) return;
 *
 * Resolves false on cancel, backdrop or Escape.
 */
export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    setValue('');
    setTouched(false);
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : (options || {});
    setValue(opts.prompt?.defaultValue || '');
    setTouched(false);
    setState({
      title: opts.title || 'Please confirm',
      message: opts.message || '',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      danger: !!opts.danger,
      icon: opts.icon || null,
      prompt: opts.prompt || null,
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const missing = !!state?.prompt?.required && !value.trim();

  const accept = useCallback(() => {
    if (missing) { setTouched(true); return; }
    settle(state?.prompt ? value.trim() : true);
  }, [missing, settle, state, value]);

  // Escape cancels. Enter confirms — except in a textarea, where it is a newline.
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        settle(false);
      } else if (e.key === 'Enter' && !(state.prompt?.multiline)) {
        e.preventDefault();
        accept();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [state, settle, accept]);

  // Put the cursor where the person has to type.
  useEffect(() => {
    if (state?.prompt && inputRef.current) inputRef.current.focus();
  }, [state]);

  const Field = state?.prompt?.multiline ? 'textarea' : 'input';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state ? (
        <ModalPortal onClose={() => settle(false)}>
          <div className="modal confirm-modal" role="alertdialog" aria-modal="true">
            <div className="confirm-modal-body">
              <div className={`confirm-modal-icon${state.danger ? ' is-danger' : ''}`}>
                <i className={`ti ${state.icon || (state.danger ? 'ti-alert-triangle' : 'ti-help-circle')}`} />
              </div>
              <div className="confirm-modal-title">{state.title}</div>
              {state.message ? (
                <div className="confirm-modal-message">{state.message}</div>
              ) : null}

              {state.prompt ? (
                <div className="confirm-modal-prompt">
                  {state.prompt.label ? (
                    <label htmlFor="confirm-prompt-field">{state.prompt.label}</label>
                  ) : null}
                  <Field
                    id="confirm-prompt-field"
                    ref={inputRef}
                    className={touched && missing ? 'has-error' : ''}
                    rows={state.prompt.multiline ? 4 : undefined}
                    value={value}
                    placeholder={state.prompt.placeholder || ''}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={() => setTouched(true)}
                  />
                  {touched && missing ? (
                    <div className="confirm-modal-error">
                      {state.prompt.requiredMessage || 'This cannot be left empty'}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="confirm-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>
                {state.cancelLabel}
              </button>
              <button
                type="button"
                className={`btn ${state.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={accept}
                disabled={missing && touched}
                autoFocus={!state.prompt}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </ConfirmContext.Provider>
  );
};

/** Returns confirm(options) => Promise<boolean | string>. Falls back to the browser if no provider. */
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  return ctx || ((options) => {
    const opts = typeof options === 'string' ? { message: options } : (options || {});
    if (opts.prompt) {
      // eslint-disable-next-line no-alert
      const typed = window.prompt(opts.prompt.label || opts.title || '', opts.prompt.defaultValue || '');
      return Promise.resolve(typed && typed.trim() ? typed.trim() : false);
    }
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(opts.message || 'Are you sure?'));
  });
};

export default ConfirmProvider;
